import React from 'react';
import { useQuery } from 'react-query';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  Work,
  People,
  Assignment,
  CheckCircle,
  Schedule,
  Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext.tsx';
import { projectsApi } from '../services/api.ts';
import { ProjectStatus, ProjectType } from '../types/index.ts';

// Stat card component
interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, onClick }) => (
  <Card 
    sx={{ 
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s',
      '&:hover': onClick ? { transform: 'translateY(-2px)' } : {},
    }}
    onClick={onClick}
  >
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="textSecondary" gutterBottom variant="h6">
            {title}
          </Typography>
          <Typography variant="h4" sx={{ color }}>
            {value}
          </Typography>
        </Box>
        <Box sx={{ color, opacity: 0.7 }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Recent projects component
const RecentProjects: React.FC<{ projects: any[] }> = ({ projects }) => {
  const navigate = useNavigate();

  if (!projects || projects.length === 0) {
    return (
      <Alert severity="info">
        No recent projects found.
      </Alert>
    );
  }

  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.COMPLETED:
        return 'success';
      case ProjectStatus.INSTALLING:
        return 'warning';
      case ProjectStatus.CANCELLED:
        return 'error';
      default:
        return 'primary';
    }
  };

  return (
    <Box>
      {projects.slice(0, 5).map((project) => (
        <Card key={project.id} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="h6" gutterBottom>
                {project.title}
              </Typography>
              <Chip 
                label={project.status_display} 
                color={getStatusColor(project.status)}
                size="small"
              />
            </Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {project.project_number} • {project.project_type_display}
            </Typography>
            {project.estimated_cost && (
              <Typography variant="body2" color="primary">
                Est. £{project.estimated_cost.toLocaleString()}
              </Typography>
            )}
          </CardContent>
          <CardActions>
            <Button 
              size="small" 
              onClick={() => navigate(`/projects/${project.id}`)}
            >
              View Details
            </Button>
          </CardActions>
        </Card>
      ))}
    </Box>
  );
};

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isManager, isSurveyor, isCustomer } = useAuth();

  // Fetch project statistics
  const { data: stats, isLoading, error } = useQuery(
    'projectStats',
    projectsApi.getProjectStats,
    {
      enabled: !isCustomer, // Customers don't need full stats
    }
  );

  if (error) {
    return (
      <Alert severity="error">
        Failed to load dashboard data. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Welcome header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Welcome back, {user?.first_name}!
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {isManager && "Manage your renewable energy projects and team"}
          {isSurveyor && "View your assigned surveys and projects"}
          {isCustomer && "Track your renewable energy installation progress"}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Statistics for managers and surveyors */}
        {(isManager || isSurveyor) && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              {isLoading ? (
                <Skeleton variant="rectangular" height={120} />
              ) : (
                <StatCard
                  title="Total Projects"
                  value={stats?.total_projects || 0}
                  icon={<Work sx={{ fontSize: 40 }} />}
                  color="#2e7d32"
                  onClick={() => navigate('/projects')}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {isLoading ? (
                <Skeleton variant="rectangular" height={120} />
              ) : (
                <StatCard
                  title="Active Projects"
                  value={
                    (stats?.stats_by_status?.installing || 0) +
                    (stats?.stats_by_status?.survey_scheduled || 0) +
                    (stats?.stats_by_status?.quoted || 0)
                  }
                  icon={<Schedule sx={{ fontSize: 40 }} />}
                  color="#ff9800"
                  onClick={() => navigate('/projects?status=installing,survey_scheduled,quoted')}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {isLoading ? (
                <Skeleton variant="rectangular" height={120} />
              ) : (
                <StatCard
                  title="Completed"
                  value={stats?.stats_by_status?.completed || 0}
                  icon={<CheckCircle sx={{ fontSize: 40 }} />}
                  color="#4caf50"
                  onClick={() => navigate('/projects?status=completed')}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {isLoading ? (
                <Skeleton variant="rectangular" height={120} />
              ) : (
                <StatCard
                  title="Pending Surveys"
                  value={stats?.stats_by_status?.survey_scheduled || 0}
                  icon={<Assignment sx={{ fontSize: 40 }} />}
                  color="#f44336"
                  onClick={() => navigate('/surveys?status=scheduled')}
                />
              )}
            </Grid>
          </>
        )}

        {/* Recent Projects */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" gutterBottom>
                Recent Projects
              </Typography>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/projects')}
                size="small"
              >
                View All
              </Button>
            </Box>
            {isLoading ? (
              <Box>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={80} sx={{ mb: 2 }} />
                ))}
              </Box>
            ) : (
              <RecentProjects projects={stats?.recent_projects || []} />
            )}
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {(isManager || isSurveyor) && (
                <>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<People />}
                    onClick={() => navigate('/customers')}
                  >
                    Manage Customers
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Work />}
                    onClick={() => navigate('/projects')}
                  >
                    View All Projects
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Assignment />}
                    onClick={() => navigate('/surveys')}
                  >
                    Manage Surveys
                  </Button>
                </>
              )}
              {isCustomer && (
                <>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Work />}
                    onClick={() => navigate('/projects')}
                  >
                    My Projects
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Assignment />}
                    onClick={() => navigate('/surveys')}
                  >
                    Scheduled Surveys
                  </Button>
                </>
              )}
            </Box>
          </Paper>

          {/* Project Types Distribution (for managers/surveyors) */}
          {(isManager || isSurveyor) && stats && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Project Types
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {Object.entries(stats.stats_by_type).map(([type, count]) => (
                  <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Typography>
                    <Chip label={String(count)} size="small" />
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
